import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ChatPanel from './components/ChatPanel';
import CollapsedRail from './components/CollapsedRail';
import InviteScreen from './components/InviteScreen';
import WaitlistLanding from './components/WaitlistLanding';
import type { DesignVariant } from './components/WaitlistLanding';
import DashboardView from './components/DashboardView';
import TeamChannelView from './components/TeamChannelView';
import CocoChatBar from './components/CocoChatBar';
import SessionSidebar from './components/SessionSidebar';
import ReconnectBanner from './components/ReconnectBanner';
import ResetConfirmDialog from './components/ResetConfirmDialog';
import SettingsModal from './components/SettingsModal';
import FileEditorPanel from './components/FileEditorPanel';
import NotesPanel from './components/NotesPanel';
import TopHeaderNav from './components/TopHeaderNav';
import { personalSessionKey, useGateway } from './hooks/useGateway';
import { useIOSKeyboard } from './hooks/useIOSKeyboard';
import { useIsTabletOrMobile } from './hooks/useIsMobile';
import { agentIdFromSessionKey, useAppStore } from './stores/appStore';
import { gatewayRef } from './utils/gatewayRef';
import type { AppView, ChatMessage, UserRole } from './stores/appStore';
import { useSettingsStore } from './stores/settingsStore';
import { usePushNotifications } from './hooks/usePushNotifications';
import { persistRedeemedInvite, redeemInviteToken } from './utils/inviteRedeem';

function sessionKeyForAgent(agentId: string): string {
  const settingsDisplayName = useSettingsStore.getState().settings.general.displayName.trim();
  const storedDisplayName = typeof window !== 'undefined'
    ? (
        window.localStorage.getItem('clawlet.displayName') ??
        window.localStorage.getItem('displayName') ??
        window.localStorage.getItem('clawlet.userName') ??
        ''
      ).trim()
    : '';
  const displayName = settingsDisplayName || storedDisplayName;

  return personalSessionKey(agentId, displayName);
}

interface PushLaunchTarget {
  agentId: string;
  cleanUrl: string;
  title?: string;
  body?: string;
}

interface PushNavigateMessage {
  type?: string;
  agentId?: string;
  url?: string;
  title?: string;
  body?: string;
}

type AutoInviteState =
  | { status: 'idle' }
  | { status: 'redeeming' }
  | { status: 'connecting' }
  | { status: 'error'; error: string };

const INVITE_QUERY_KEYS = [
  'token',
  'inviteToken',
  'gatewayUrl',
  'gatewayToken',
  'displayName',
  'role',
  'allowedAgents',
];

function cleanInviteQueryParams() {
  const url = new URL(window.location.href);
  for (const key of INVITE_QUERY_KEYS) {
    url.searchParams.delete(key);
  }
  const search = url.searchParams.toString();
  window.history.replaceState({}, '', `${url.pathname}${search ? `?${search}` : ''}${url.hash}`);
}

function AutoInviteConnectScreen({
  state,
  connectionError,
}: {
  state: Exclude<AutoInviteState, { status: 'idle' }>;
  connectionError: string | null;
}) {
  const error = state.status === 'error' ? state.error : connectionError;
  const message = state.status === 'redeeming'
    ? 'Checking your invite link...'
    : 'Opening your workspace...';

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6 space-y-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Clawlet</h1>
          <p className="text-sm text-secondary-foreground">{error ? 'Invite link failed.' : message}</p>
        </div>

        {error ? (
          <p className="text-sm text-destructive-foreground">{error}</p>
        ) : (
          <div className="flex items-center gap-3 text-sm text-secondary-foreground">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
            <span>Connecting...</span>
          </div>
        )}
      </div>
    </div>
  );
}

function parsePushLaunchTarget(rawUrl: string, fallbackAgentId?: string, title?: string, body?: string): PushLaunchTarget | null {
  const currentUrl = new URL(rawUrl, window.location.origin);
  const from = currentUrl.searchParams.get('from');
  const pathMatch = currentUrl.pathname.match(/^\/chat\/([^/]+)$/);
  const agentFromPath = pathMatch?.[1] ? decodeURIComponent(pathMatch[1]) : null;
  const agentId = currentUrl.searchParams.get('agent') ?? agentFromPath ?? fallbackAgentId ?? null;

  if (from !== 'push' || !agentId) {
    return null;
  }

  currentUrl.searchParams.delete('agent');
  currentUrl.searchParams.delete('from');

  if (pathMatch) {
    currentUrl.pathname = '/';
  }

  const search = currentUrl.searchParams.toString();
  return {
    agentId,
    cleanUrl: `${currentUrl.pathname}${search ? `?${search}` : ''}${currentUrl.hash}`,
    title,
    body,
  };
}

function PushPromptBanner({
  onEnable,
  onDismiss,
  busy,
}: {
  onEnable: () => Promise<void>;
  onDismiss: () => void;
  busy: boolean;
}) {
  return (
    <div
      className="mx-3 mt-3 rounded-xl border px-4 py-3"
      style={{
        borderColor: 'var(--theme-border)',
        background: 'var(--theme-card)',
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span style={{ fontSize: '0.95rem' }}>🔔</span>
            <span className="text-sm font-semibold" style={{ color: 'var(--theme-foreground)' }}>
              Turn on notifications?
            </span>
          </div>
          <p className="mt-1 text-xs" style={{ color: 'var(--theme-muted-foreground)' }}>
            Get agent pings on this device and jump straight into the right chat when you tap one.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-lg border px-3 py-2 text-sm"
            style={{
              borderColor: 'var(--theme-border)',
              background: 'var(--theme-background)',
              color: 'var(--theme-card-foreground)',
            }}
          >
            Not now
          </button>
          <button
            type="button"
            onClick={() => void onEnable()}
            disabled={busy}
            className="rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-60"
            style={{
              background: 'var(--theme-primary)',
              color: 'var(--theme-primary-foreground)',
            }}
          >
            {busy ? 'Enabling…' : 'Enable'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Pending fork context — keyed by session key, consumed on first user message */
const pendingForkContexts = new Map<string, string>();

function formatConversationTranscript(messages: ChatMessage[]): string {
  const parts: string[] = [];
  for (const msg of messages) {
    const role = msg.role === 'user' ? 'User' : msg.role === 'assistant' ? 'Assistant' : 'System';
    const content = msg.content.trim();
    if (!content && (!msg.toolCalls || msg.toolCalls.length === 0)) continue;

    let entry = `**${role}:**\n${content}`;

    if (msg.toolCalls && msg.toolCalls.length > 0) {
      const toolSummaries = msg.toolCalls.map((tc) => {
        let summary = `  - ${tc.name} [${tc.status}]`;
        if (tc.params) {
          const paramStr = JSON.stringify(tc.params);
          // Truncate very long params
          summary += ` params: ${paramStr.length > 200 ? paramStr.slice(0, 200) + '...' : paramStr}`;
        }
        if (tc.result !== undefined) {
          const resultStr = typeof tc.result === 'string' ? tc.result : JSON.stringify(tc.result);
          summary += `\n    result: ${resultStr.length > 300 ? resultStr.slice(0, 300) + '...' : resultStr}`;
        }
        return summary;
      });
      entry += `\n\nTool calls:\n${toolSummaries.join('\n')}`;
    }

    parts.push(entry);
  }
  return parts.join('\n\n---\n\n');
}

export default function App() {
  const {
    connect,
    reconnect,
    disconnect,
    sendMessage,
    sendBtwMessage,
    sendSilentMessage,
    refreshSessions,
    loadHistoryForSession,
    getFullSessionTranscript,
    abortChat,
    connected,
    connecting,
  } = useGateway();

  const setActiveSessionKey = useAppStore((state) => state.setActiveSessionKey);
  const hasConnectedOnce = useAppStore((state) => state.hasConnectedOnce);
  const leftPanelCollapsed = useAppStore((state) => state.leftPanelCollapsed);
  const leftPanelWidth = useAppStore((state) => state.leftPanelWidth);
  const setLeftPanelWidth = useAppStore((state) => state.setLeftPanelWidth);
  const toggleLeftPanel = useAppStore((state) => state.toggleLeftPanel);

  // Drag-to-resize state for the left panel
  const [isDragging, setIsDragging] = useState(false);
  const [dragWidth, setDragWidth] = useState(leftPanelWidth);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(0);
  const rafRef = useRef(0);

  const effectiveWidth = useMemo(() => {
    if (leftPanelCollapsed) return 48;
    const w = isDragging ? dragWidth : leftPanelWidth;
    return Math.min(w, typeof window !== 'undefined' ? window.innerWidth * 0.4 : 480);
  }, [leftPanelCollapsed, isDragging, dragWidth, leftPanelWidth]);

  const handleResizePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragStartXRef.current = e.clientX;
    dragStartWidthRef.current = leftPanelWidth;
    setIsDragging(true);
  }, [leftPanelWidth]);

  useEffect(() => {
    if (!isDragging) return;

    const onMove = (e: PointerEvent) => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const delta = e.clientX - dragStartXRef.current;
        const raw = dragStartWidthRef.current + delta;
        setDragWidth(Math.max(180, Math.min(480, raw)));
      });
    };
    const onUp = () => {
      cancelAnimationFrame(rafRef.current);
      setIsDragging(false);
      setLeftPanelWidth(dragWidth);
    };
    const onCancel = () => {
      cancelAnimationFrame(rafRef.current);
      setIsDragging(false);
      setDragWidth(leftPanelWidth);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('lostpointercapture', onCancel);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('lostpointercapture', onCancel);
    };
  }, [isDragging, dragWidth, leftPanelWidth, setLeftPanelWidth]);
  const mobileOverlay = useAppStore((state) => state.mobileOverlay);
  const setMobileOverlay = useAppStore((state) => state.setMobileOverlay);
  const setUserRole = useAppStore((state) => state.setUserRole);
  const currentView = useAppStore((state) => state.currentView);
  const setSelectedAgentId = useAppStore((state) => state.setSelectedAgentId);
  const setCurrentView = useAppStore((state) => state.setCurrentView);
  const gatewayToken = useAppStore((state) => state.gatewayToken);
  const connectionError = useAppStore((state) => state.connectionError);

  const isTabletOrMobile = useIsTabletOrMobile();
  useIOSKeyboard();

  const pushNotifications = usePushNotifications({ connected, gatewayToken });
  const initialInviteState = useMemo<AutoInviteState>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('token')) {
      return { status: 'redeeming' };
    }
    if (params.get('gatewayUrl') && params.get('gatewayToken')) {
      return { status: 'connecting' };
    }
    return { status: 'idle' };
  }, []);
  const [autoInviteState, setAutoInviteState] = useState<AutoInviteState>(
    () => initialInviteState,
  );
  const initialPushLaunch = useMemo(() => parsePushLaunchTarget(window.location.href), []);
  const [pendingPushLaunch, setPendingPushLaunch] = useState<PushLaunchTarget | null>(initialPushLaunch);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlInviteTokenDirect = params.get('token');
    const urlGatewayUrl = params.get('gatewayUrl');
    const urlGatewayToken = params.get('gatewayToken');
    const urlDisplayName = params.get('displayName');
    const urlInviteToken = params.get('inviteToken');
    const urlAllowedAgents = params.get('allowedAgents');
    const urlRole = params.get('role') as UserRole | null;
    const urlAllowedAgentIds = urlAllowedAgents
      ? urlAllowedAgents.split(',').map((id) => id.trim()).filter(Boolean)
      : [];

    const urlView = params.get('view');
    if (urlView === 'dashboard' || urlView === 'assets' || urlView === 'chat' || urlView === 'team') {
      useAppStore.getState().setCurrentView(urlView as AppView);
    }

    if (urlInviteTokenDirect) {
      void (async () => {
        setAutoInviteState({ status: 'redeeming' });

        try {
          const inviteOrigin = window.location.origin;
          const data = await redeemInviteToken(inviteOrigin, urlInviteTokenDirect);
          const finalName = data.display_name || 'Friend';

          persistRedeemedInvite({
            token: urlInviteTokenDirect,
            inviteOrigin,
            role: urlRole,
            displayName: finalName,
            allowedAgents: data.allowed_agents,
            gatewayUrl: data.gateway_url,
            gatewayToken: data.gateway_token,
          });

          cleanInviteQueryParams();
          setAutoInviteState({ status: 'connecting' });
          connect(data.gateway_url, data.gateway_token);
        } catch (err) {
          setAutoInviteState({
            status: 'error',
            error: err instanceof Error ? err.message : 'Something went wrong.',
          });
        }
      })();
      return;
    }

    if (urlRole) {
      setUserRole(urlRole === 'builder' ? 'builder' : 'member');
    }

    if (urlAllowedAgentIds.length > 0) {
      useAppStore.getState().setAllowedAgents(urlAllowedAgentIds);
    }

    if (urlGatewayUrl && urlGatewayToken) {
      setAutoInviteState({ status: 'connecting' });

      if (!urlRole && urlDisplayName) {
        setUserRole('member');
      }

      useAppStore.getState().setGatewayUrl(urlGatewayUrl);
      useAppStore.getState().setGatewayToken(urlGatewayToken);
      if (urlDisplayName) {
        localStorage.setItem('clawlet.displayName', urlDisplayName);
        localStorage.setItem('clawlet.fromInvite', '1');
        useSettingsStore.getState().updateGeneral({ displayName: urlDisplayName });
      }
      if (urlInviteToken) {
        localStorage.setItem('clawlet.inviteToken', urlInviteToken);
      }

      window.history.replaceState({}, '', window.location.pathname);
      connect(urlGatewayUrl, urlGatewayToken);
      return;
    }

    if (urlAllowedAgentIds.length > 0) {
      params.delete('allowedAgents');
      const nextSearch = params.toString();
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`,
      );
    }

    const url = useAppStore.getState().gatewayUrl;
    const token = useAppStore.getState().gatewayToken;
    if (url && token && !connected && !hasConnectedOnce) {
      connect(url, token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return undefined;
    }

    const handleServiceWorkerMessage = (event: MessageEvent<PushNavigateMessage>) => {
      const data = event.data;
      if (!data || data.type !== 'push-navigate') {
        return;
      }

      const pushTarget = parsePushLaunchTarget(data.url || window.location.href, data.agentId, data.title, data.body);
      if (!pushTarget) {
        return;
      }

      setPendingPushLaunch(pushTarget);
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);

  useEffect(() => {
    if (connected) {
      try {
        localStorage.removeItem('clawlet.inviteToken');
      } catch {
        // localStorage not available
      }

      const inviteDisplayName = useSettingsStore.getState().settings.general.displayName.trim();
      const isFromInvite = localStorage.getItem('clawlet.fromInvite');
      if (inviteDisplayName && isFromInvite) {
        localStorage.removeItem('clawlet.fromInvite');

        const allowedAgents = useAppStore.getState().allowedAgents;
        const firstAgent = allowedAgents.length > 0 ? allowedAgents[0] : 'yuki';
        const personalKey = personalSessionKey(firstAgent, inviteDisplayName);
        setActiveSessionKey(personalKey);

        setTimeout(() => {
          sendSilentMessage(personalKey, `[System: The user "${inviteDisplayName}" just joined via an invite link. Greet them warmly by name! This is their first time using Clawlet.]`);
        }, 3000);
      }
    }
  }, [connected, sendSilentMessage, setActiveSessionKey]);

  useEffect(() => {
    if (!connected || !pendingPushLaunch) {
      return;
    }

    const { agentId, cleanUrl, title, body } = pendingPushLaunch;
    const sessionKey = sessionKeyForAgent(agentId);

    setPendingPushLaunch(null);
    setCurrentView('chat');
    setSelectedAgentId(agentId);
    setActiveSessionKey(sessionKey);
    if (isTabletOrMobile) {
      setMobileOverlay('none');
    }

    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (currentUrl !== cleanUrl) {
      window.history.replaceState({}, '', cleanUrl);
    }

    void loadHistoryForSession(sessionKey).catch((error) => {
      console.debug('[App] Push deep-link history load failed:', error);
    });

    const notifContext = title && body
      ? `I just tapped a notification from "${title}". Here's what it said:\n\n---\n${body}\n---\n\nShow me the full content above and ask what I'd like to do. Don't take any actions yet.`
      : 'Hey! I just tapped your notification and I am here now!';

    void sendSilentMessage(sessionKey, notifContext);
  }, [
    connected,
    isTabletOrMobile,
    loadHistoryForSession,
    pendingPushLaunch,
    sendSilentMessage,
    setActiveSessionKey,
    setCurrentView,
    setMobileOverlay,
    setSelectedAgentId,
  ]);

  const handleNewTab = useCallback(
    async (mode: 'new' | 'sameContext') => {
      const state = useAppStore.getState();
      const agentId = state.selectedAgentId
        ?? (state.activeSessionKey ? agentIdFromSessionKey(state.activeSessionKey) : 'main');
      const newSessionKey = `agent:${agentId}:chat-${Date.now()}`;

      if (mode === 'sameContext' && state.activeSessionKey) {
        const currentMessages = state.messages[state.activeSessionKey] ?? [];
        if (currentMessages.length > 0) {
          // Copy messages into the new tab UI (marked as history)
          const copiedMessages = currentMessages.map((m) => ({ ...m, isHistory: true }));
          state.replaceMessages(newSessionKey, copiedMessages);

          // Clone the CLI session JSONL via gateway so the fork preserves
          // full structured history (tool_use/tool_result pairs, thinking blocks).
          // Falls back to text transcript if sessions.fork is unavailable.
          let forkSucceeded = false;
          try {
            const client = gatewayRef.current;
            if (client?.connected) {
              await client.request('sessions.fork', {
                sourceKey: state.activeSessionKey,
                targetKey: newSessionKey,
              });
              forkSucceeded = true;
            }
          } catch {
            // sessions.fork not available — fall back to text transcript
          }

          if (!forkSucceeded) {
            const fullTranscript = await getFullSessionTranscript(state.activeSessionKey);
            const transcript = fullTranscript || formatConversationTranscript(currentMessages);
            if (transcript) {
              pendingForkContexts.set(newSessionKey, transcript);
            }
          }
        }
      }

      state.addChatTab(newSessionKey);
      setActiveSessionKey(newSessionKey);
      useAppStore.getState().setCurrentView('chat');
      void loadHistoryForSession(newSessionKey);
    },
    [setActiveSessionKey, loadHistoryForSession, getFullSessionTranscript],
  );

  const handleSessionSelect = useCallback(
    async (sessionKey: string) => {
      setActiveSessionKey(sessionKey);
      await loadHistoryForSession(sessionKey);
      if (isTabletOrMobile) {
        setMobileOverlay('none');
      }
    },
    [isTabletOrMobile, loadHistoryForSession, setActiveSessionKey, setMobileOverlay],
  );

  const sendMessageWithForkContext = useCallback(
    async (text: string, attachments?: import('@clawlet/gateway-client').Attachment[]) => {
      const sessionKey = useAppStore.getState().activeSessionKey;
      const forkContext = sessionKey ? pendingForkContexts.get(sessionKey) : null;
      if (forkContext && sessionKey) {
        pendingForkContexts.delete(sessionKey);
        const contextMessage = `[Context fork — this session continues from an existing conversation. Below is the full transcript so far.]\n\n${forkContext}\n\n---\n\n[IMPORTANT: This is a forked session. You already have the full conversation context above. Skip the "Every Session" startup routine (no need to read AGENTS.md, memory files, dashboard, or PLAN.md — you already have all the context). Respond directly to the user's message below.]\n\n${text}`;
        return sendMessage(contextMessage, attachments);
      }
      return sendMessage(text, attachments);
    },
    [sendMessage],
  );

  const currentParams = new URLSearchParams(window.location.search);
  const hasInviteParams = Boolean(currentParams.get('gatewayUrl') || currentParams.get('token'));
  const hasStoredCredentials = Boolean(
    useAppStore.getState().gatewayUrl && useAppStore.getState().gatewayToken,
  );

  if (!connected && !hasConnectedOnce && !connecting && !hasStoredCredentials && !hasInviteParams) {
    // Show waitlist landing only when explicitly requested via ?landing or #waitlist
    const params = new URLSearchParams(window.location.search);
    const showLanding = params.has('landing') || window.location.hash === '#waitlist';
    if (showLanding) {
      const designParam = params.get('design') as DesignVariant | null;
      const variant: DesignVariant = (designParam === 'A' || designParam === 'B' || designParam === 'C') ? designParam : 'A';
      return <WaitlistLanding variant={variant} />;
    }
    return <InviteScreen onConnect={connect} connecting={connecting} />;
  }

  if (autoInviteState.status !== 'idle' && !connected) {
    return <AutoInviteConnectScreen state={autoInviteState} connectionError={connectionError} />;
  }

  const renderMainView = () => {
    if (currentView === 'dashboard') {
      return <DashboardView />;
    }

    if (currentView === 'team') {
      return <TeamChannelView />;
    }

    return (
      <ChatPanel
        connected={connected}
        sendMessage={sendMessageWithForkContext}
        sendBtwMessage={sendBtwMessage}
        abortChat={abortChat}
      />
    );
  };

  if (isTabletOrMobile) {
    const drawerOpen = mobileOverlay === 'sessions';

    return (
      <div
        className="flex min-h-0 flex-col"
        style={{
          height: 'var(--app-height, 100dvh)',
          background: 'var(--theme-background)',
          color: 'var(--theme-foreground)',
        }}
      >
        <ReconnectBanner onReconnect={reconnect} />
        <ResetConfirmDialog />
        {pushNotifications.promptVisible ? (
          <PushPromptBanner
            onEnable={pushNotifications.enablePush}
            onDismiss={pushNotifications.dismissPrompt}
            busy={pushNotifications.isBusy}
          />
        ) : null}
        <SettingsModal sendMessage={sendMessage} pushNotifications={pushNotifications} />
        <FileEditorPanel />
        <NotesPanel />

        <TopHeaderNav
          onMenuClick={() => setMobileOverlay(drawerOpen ? 'none' : 'sessions')}
          menuLabel={drawerOpen ? 'Close sidebar' : 'Open sidebar'}
          onTabSelect={handleSessionSelect}
          onNewTab={handleNewTab}
        />

        <main className="flex-1 min-h-0 min-w-0 flex flex-col">
          {renderMainView()}
        </main>

        {drawerOpen && <div className="drawer-backdrop" onClick={() => setMobileOverlay('none')} />}

        <aside className={`sidebar-drawer${drawerOpen ? ' is-open' : ''}`}>
          <SessionSidebar
            onSelectSession={handleSessionSelect}
            refreshSessions={refreshSessions}
            onCollapse={() => setMobileOverlay('none')}
          />
        </aside>

        {currentView === 'dashboard' ? <CocoChatBar /> : null}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--theme-background)', color: 'var(--theme-foreground)' }}>
      <ReconnectBanner onReconnect={reconnect} />
      <ResetConfirmDialog />
      {pushNotifications.promptVisible ? (
        <PushPromptBanner
          onEnable={pushNotifications.enablePush}
          onDismiss={pushNotifications.dismissPrompt}
          busy={pushNotifications.isBusy}
        />
      ) : null}
      <SettingsModal sendMessage={sendMessage} pushNotifications={pushNotifications} />
      <FileEditorPanel />
      <NotesPanel />

      <TopHeaderNav
        onMenuClick={toggleLeftPanel}
        menuLabel={leftPanelCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        onTabSelect={handleSessionSelect}
        onNewTab={handleNewTab}
      />

      <div className="flex-1 min-h-0 flex">
        <aside
          className={isDragging ? 'overflow-hidden relative' : 'transition-all duration-300 ease-in-out overflow-hidden relative'}
          style={{ width: `${effectiveWidth}px`, minWidth: `${effectiveWidth}px` }}
        >
          {leftPanelCollapsed ? (
            <CollapsedRail side="left" onExpand={toggleLeftPanel} onSelectSession={handleSessionSelect} />
          ) : (
            <>
              <SessionSidebar
                onSelectSession={handleSessionSelect}
                refreshSessions={refreshSessions}
                onCollapse={toggleLeftPanel}
              />
              {/* Drag handle for resizing */}
              <div
                onPointerDown={handleResizePointerDown}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  width: '5px',
                  height: '100%',
                  cursor: 'col-resize',
                  zIndex: 10,
                  background: isDragging ? 'var(--theme-primary)' : 'transparent',
                  opacity: isDragging ? 0.5 : 1,
                  transition: isDragging ? 'none' : 'background 0.15s',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--theme-border)'; }}
                onMouseLeave={(e) => { if (!isDragging) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              />
            </>
          )}
        </aside>

        <main className="flex-1 min-w-0 min-h-0 flex flex-col">
          {renderMainView()}
        </main>
      </div>

      {currentView === 'dashboard' ? <CocoChatBar /> : null}
    </div>
  );
}
